import { useCallback, useState } from 'react'
import { confirmActionModal, createId } from '@util'
import {
  isNotDefined,
  type DashboardDataWithEditable,
  type DashboardId,
  type DashboardListInput,
  type DashboardScopeId,
  type UserId,
} from '@terros/common'
import { useToastError } from '@hooks'
import {
  buildHustleWidgetCopy,
  getHustleFilterForScope,
  useAddDashboard,
  useTranslation,
  useUpdateDashboard,
  type HustleDashboards,
} from '@front/model'

type Props = {
  hustle: HustleDashboards
  listInput: DashboardListInput | undefined
  scopeEntityId: DashboardScopeId | undefined
  viewerUserId: UserId | undefined
}

type HustleActions = {
  copyingDashboardId: DashboardId | undefined
  isCreating: boolean
  /** Creates the scope's hustle dashboard and returns it, or `undefined` on failure. */
  createHustle: () => Promise<DashboardDataWithEditable | undefined>
  /** Copies a handed-down hustle widget onto the scope's own, replacing it if present. */
  copyHustle: (source: DashboardDataWithEditable) => Promise<void>
}

export function useHustleActions({ hustle, listInput, scopeEntityId, viewerUserId }: Props): HustleActions {
  const { t } = useTranslation()
  const { add, isAdding, error: addError } = useAddDashboard(listInput)
  const { update, isUpdating, error: updateError } = useUpdateDashboard(listInput)
  const [copyingDashboardId, setCopyingDashboardId] = useState<DashboardId | undefined>(undefined)
  useToastError(addError)
  useToastError(updateError)

  const scopeId = scopeEntityId ?? viewerUserId
  const filter = getHustleFilterForScope({ scopeEntityId, viewerUserId })

  const createHustle = useCallback(async (): Promise<DashboardDataWithEditable | undefined> => {
    if (isNotDefined(scopeId)) return undefined
    try {
      return await add({
        unsaved: {
          scopeId,
          filter,
          home: true,
          widgets: [{ widgetId: createId('DashWidget'), type: 'number', metrics: [] }],
        },
      })
    } catch {
      return undefined
    }
  }, [add, filter, scopeId])

  const copyHustle = useCallback(
    async (source: DashboardDataWithEditable): Promise<void> => {
      const sourceWidget = source.widgets[0]
      if (isNotDefined(sourceWidget)) {
        return
      }
      if (isNotDefined(scopeId)) return

      // "Use as Mine" replaces the scope's own hustle when there is one, and
      // builds a fresh one otherwise.
      const own = hustle.own
      if (own) {
        const confirmed = await confirmActionModal({
          title: t('Replace Your Hustle Widget?'),
          message: t('Your current Hustle widget will be replaced by this one. You can edit it afterward.'),
          label: t('Replace'),
          t,
        })
        if (!confirmed) return
      }

      setCopyingDashboardId(source.dashboardId)
      const widget = buildHustleWidgetCopy({
        source: sourceWidget,
        widgetId: createId('DashWidget'),
        existingName: own?.widgets[0]?.name,
      })

      try {
        if (own) {
          await update({ ...own, widgets: [widget], filter })
          return
        }
        await add({
          unsaved: {
            dateRange: source.dateRange,
            scopeId,
            filter,
            home: true,
            widgets: [widget],
          },
        })
      } catch {
        return
      } finally {
        setCopyingDashboardId(undefined)
      }
    },
    [add, filter, hustle.own, scopeId, t, update]
  )

  return {
    copyingDashboardId,
    isCreating: isAdding || isUpdating,
    createHustle,
    copyHustle,
  }
}
